import React, { useEffect, useState } from "react";
import {
  Tabs,
  Input,
  Row,
  Col,
  Card,
  Tag,
  Dropdown,
  Button,
  Space,
  Spin,
} from "antd";
import {
  MoreOutlined,
  CheckCircleOutlined,
  StopFilled,
} from "@ant-design/icons";
import axios from "axios";
import ProductCategoryCard from "./CategoryCard.js";
import "../../config";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;
const { Search } = Input;

const ProductCategory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [productCategory, setProductCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollRestored, setScrollRestored] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // First, try to restore scroll position immediately
    const savedScroll = sessionStorage.getItem("categoryListScroll");
    console.log("=== COMPONENT MOUNT ===");
    console.log("Saved scroll on mount:", savedScroll);

    if (savedScroll && savedScroll !== "0") {
      const scrollY = parseInt(savedScroll, 10);
      console.log("Attempting immediate scroll restore to:", scrollY);
      window.scrollTo(0, scrollY);
    }

    setLoading(true);
    getListofProductCategory();
    document.title = "JOJ | Category List";

    // Only log user activity if token is present
    const tokenString = localStorage.getItem("token");
    if (tokenString) {
      logUserActivity();
    }

    // Throttled scroll handling to prevent too many saves
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY > 50) {
          // Increased threshold to avoid saving small scroll values
          sessionStorage.setItem("categoryListScroll", scrollY.toString());
          console.log("Scroll position saved:", scrollY);
        }
      }, 100);
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);

      // FIXED: Only save scroll position on unmount if it's significantly greater than 50
      // and if there isn't already a larger value saved
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const currentSaved = parseInt(
        sessionStorage.getItem("categoryListScroll") || "0",
        10
      );

      if (scrollY > 50 && scrollY > currentSaved) {
        sessionStorage.setItem("categoryListScroll", scrollY.toString());
        console.log("Scroll saved on unmount:", scrollY);
      } else {
        console.log(
          "Skipping scroll save on unmount - current:",
          scrollY,
          "saved:",
          currentSaved
        );
      }
    };
  }, []);

  // Enhanced scroll restoration after data loads
  useEffect(() => {
    if (!loading && productCategory.length > 0 && !scrollRestored) {
      console.log("=== POST-DATA SCROLL RESTORATION ===");

      const restoreScroll = () => {
        const savedScroll = sessionStorage.getItem("categoryListScroll");
        console.log("Attempting to restore scroll:", savedScroll);

        if (savedScroll && parseInt(savedScroll, 10) > 50) {
          // Only restore if > 50
          const scrollY = parseInt(savedScroll, 10);
          console.log("Restoring scroll to:", scrollY);

          window.scrollTo({
            top: scrollY,
            behavior: "auto",
          });

          setScrollRestored(true);

          // Verify restoration after a delay
          setTimeout(() => {
            const currentScroll =
              window.pageYOffset || document.documentElement.scrollTop;
            console.log(
              "Scroll after restoration verification:",
              currentScroll
            );
          }, 200);
        } else {
          console.log("No valid scroll position to restore (value too small)");
          setScrollRestored(true);
        }
      };

      // Multiple restoration attempts with increasing delays
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 150);
      setTimeout(restoreScroll, 300);
      setTimeout(restoreScroll, 500);
    }
  }, [loading, productCategory, scrollRestored]);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      const jsondata = JSON.stringify({
        ScreenID: "1",
        DetailID: "0",
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Combine all categories from all sections, then sort by SortingNo
  function getCombinedSortedCategories() {
    return (productCategory || [])
      .flatMap((section) =>
        (section.data || []).map((cat) => ({
          ...cat,
          sectionTitle: section.title,
          SortingNumber: cat.SortingNo,
        }))
      )
      .sort((a, b) => (a.SortingNumber ?? 0) - (b.SortingNumber ?? 0));
  }

  const combinedSortedCategories = getCombinedSortedCategories();

  async function getListofProductCategory() {
    setLoading(true);
    const tokenString = localStorage.getItem("token");

    try {
      let config;
      if (tokenString) {
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/ProductCategory/ListofProductCategory",
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        };
      } else {
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/PublicData/ListofProductCategory",
        };
      }

      axios
        .request(config)
        .then((response) => {
          setProductCategory(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token");
          }
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
    }
  }

  const totalCount = productCategory.reduce(
    (acc, section) => acc + section.data.length,
    0
  );

  // Handle category card click
  const handleCategoryClick = (category) => {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    console.log("=== CATEGORY CLICK ===");
    console.log("Current scroll position:", scrollY);

    // Always save the scroll position when user intentionally navigates
    sessionStorage.setItem("categoryListScroll", scrollY.toString());

    // Verify it was saved
    const saved = sessionStorage.getItem("categoryListScroll");
    console.log("Verified saved value:", saved);

    // Store category ID
    sessionStorage.setItem("CategoryID", category.ProductCategoryID);

    // Navigate
    navigate(`/shop/${category.CategoryName}/${category.ProductCategoryID}`);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Spin tip="Loading..." spinning={loading}>
          {/* Fix the Tabs deprecation warning */}
          <Tabs
            defaultActiveKey="All"
            onChange={(key) => setActiveTab(key)}
            style={{ marginBottom: 16 }}
            items={[
              {
                key: "All",
                label: `All (${combinedSortedCategories.length})`,
              },
            ]}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 24,
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <Search
              placeholder="Search category..."
              onSearch={(value) => setSearchTerm(value)}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: 300 }}
              allowClear
            />
            <Button>Actions</Button>
          </div>

          <Row gutter={[16, 16]} justify="center">
            {combinedSortedCategories
              .filter((cat) =>
                cat.CategoryName.toLowerCase().includes(
                  searchTerm.toLowerCase()
                )
              )
              .map((category) => (
                <Col
                  key={category.ProductCategoryID}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                >
                  <ProductCategoryCard
                    category={category}
                    onClick={() => handleCategoryClick(category)}
                  />
                </Col>
              ))}
          </Row>
        </Spin>
      </Card>
    </div>
  );
};

export default ProductCategory;
